<template>
  <div>
    <Loader v-if="isLoading" class="login-modal__loader" />

    <Transition name="fade-modal-content">
      <div v-if="!isOTPScreen" class="login-modal">
        <div class="login-modal__inner">
          <h2 class="login-modal__title" v-html="$t(title)" />
          <p
            v-if="isAuthEmailEnabled"
            class="login-modal__subtitle"
            v-html="$t('modal.login.subtitle')"
          />
        </div>
        <AuthEmailForm
          v-if="isAuthEmailEnabled"
          :email-error-messages="emailErrorMessages"
          :loading="isSendingEmail"
          @submit="sendEmailOtp"
        />
        <div class="login-modal__login-buttons login-buttons">
          <UiSocialButton
            v-if="isGoogleAuthAvailable"
            social="google"
            @click="loginWithGoogle"
          />
          <UiSocialButton
            v-if="isAppleAuthAvailable"
            social="apple"
            @click="loginWithApple"
          />
        </div>
        <div class="login-modal__terms">
          <p>
            {{ $t('modal.continuing_registration_agree_text') }}
            <a
              href="https://imast.am/terms"
              target="_blank"
              class="link"
              v-html="$t('modal.terms')"
            />
            {{ $t('and') }}&nbsp;
            <a
              href="https://imast.am/privacy"
              target="_blank"
              class="link"
              v-html="$t('modal.policy')"
            />
          </p>
        </div>
      </div>
    </Transition>

    <Transition name="fade-modal-content">
      <ModalsOTP
        v-if="isOTPScreen"
        :email="userEmail"
        :timeout="timerTimeout"
        :error="emailErrorMessages"
        @back="backToLoginWindow"
        @submit-email-otp="sendEmailOtp"
        @send-otp="sendOtp"
      />
    </Transition>
  </div>
</template>

<script setup>
import useFeaturesStore from '~/store/features';

const { $useApi } = useNuxtApp();
const { applyAuthSession } = useAuthSession();
const googleAuth = useGoogleAuthRedirect();

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits(['login']);

const props = defineProps({
  title: {
    type: String,
    default: 'modal.login.title',
  },
  referrer: {
    type: String,
    default: null,
  },
  sendUtmData: {
    type: Function,
    default: null,
  },
  utmData: {
    type: Object,
    default: null,
  },
  initialLoading: {
    type: Boolean,
    default: false,
  },
});

const url = useRequestURL();
const config = useRuntimeConfig();
const googleClientId = config.public.GOOGLE_CLIENT_ID;
const appleClientId = config.public.APPLE_CLIENT_ID;
const isAuthEmailEnabled = useFeaturesStore().authEmailEnabled;
const isGoogleAuthAvailable = useFeaturesStore().authGoogleEnabled;
const isAppleAuthAvailable = useFeaturesStore().authAppleEnabled;

const appleRequestParams = {
  clientId: appleClientId,
  scope: 'name email',
  redirectURI: url.origin,
  usePopup: true,
};

const isOTPScreen = ref(false);
const isLoading = ref(props.initialLoading);
const isSendingEmail = ref(false);
const userEmail = ref(null);
const timerTimeout = ref(null);
const timerId = ref(null);
const emailErrorMessages = ref(null);

const refererUser = computed(() => props.referrer || useRoute().query?.ref);

onMounted(async () => {
  const pendingGoogle = googleAuth.peekResume();
  if (pendingGoogle?.code) isLoading.value = true;

  const resumePromise = pendingGoogle
    ? nextTick().then(() => googleAuth.consumeResume())
    : null;

  const scriptsReady = Promise.all([
    useLazyScript('https://accounts.google.com/gsi/client'),
    useLazyScript(
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
    ),
  ])
    .then(() => {
      window.AppleID?.auth.init(appleRequestParams);
    })
    .catch((error) => {
      console.error(error);
    });

  const currentGoogle = await resumePromise;
  if (currentGoogle?.code) {
    await login(
      {
        code: currentGoogle.code,
        redirect_uri: googleAuth.getRedirectUri(),
      },
      'google',
    );
  } else if (pendingGoogle) {
    isLoading.value = false;
  }

  await scriptsReady;
});

onUnmounted(() => {
  if (timerId.value) {
    clearInterval(timerId.value);
    timerId.value = null;
  }
});

async function sendEmailOtp(email) {
  isSendingEmail.value = true;

  try {
    const { timeout } = await $fetch($useApi.auth.sendEmailOTP, {
      method: 'POST',
      headers: useCustomRequestHeaders(),
      body: {
        email,
      },
    });

    userEmail.value = email;
    timerTimeout.value = timeout;
    isOTPScreen.value = true;
    emailErrorMessages.value = null;

    if (timerId.value) clearInterval(timerId.value);
    startTimer();
  } catch (error) {
    const code = error?.response?.status;

    switch (code) {
      case 429:
        emailErrorMessages.value = 'error.email_ban';
        isOTPScreen.value = false;
        break;
      case 422:
        emailErrorMessages.value = 'error.email';
        break;
      default:
        console.error('Unhandled OTP error:', error);
    }
  } finally {
    isSendingEmail.value = false;
  }
}

function sendOtp(otp) {
  isLoading.value = true;
  login({ email: userEmail.value, otp }, 'email');
}

function startTimer() {
  timerId.value = setInterval(() => {
    timerTimeout.value -= 1;
    if (timerTimeout.value <= 0) {
      timerTimeout.value = null;
      clearInterval(timerId.value);
      timerId.value = null;
    }
  }, 1000);
}

function loginWithGoogle() {
  if (!window.google) return;

  if (shouldUseGoogleRedirect()) {
    startGoogleRedirect();
    return;
  }

  window.google.accounts.oauth2
    .initCodeClient({
      client_id: googleClientId,
      scope: 'email profile',
      ux_mode: 'popup',
      prompt: 'consent',
      callback: ({ code, error }) => {
        if (error || !code) {
          if (error) console.error('Error:', error);
          return;
        }

        login({ code, redirect_uri: 'postmessage' }, 'google');
      },
      error_callback: (error) => {
        console.error(error);
        if (error?.type === 'popup_failed_to_open') {
          startGoogleRedirect();
        }
      },
    })
    .requestCode();
}

function startGoogleRedirect() {
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const state = googleAuth.begin(returnTo);

  window.google.accounts.oauth2
    .initCodeClient({
      client_id: googleClientId,
      scope: 'email profile',
      ux_mode: 'redirect',
      redirect_uri: googleAuth.getRedirectUri(),
      state,
      prompt: 'consent',
    })
    .requestCode();
}

function loginWithApple() {
  if (window.AppleID) {
    window.AppleID.auth
      .signIn()
      .then((response) => {
        if (response.authorization) {
          const { code } = response.authorization;
          login({ code }, 'apple');
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }
}

async function login(data, type) {
  let authApiUrl;
  let signin_method;

  switch (type) {
    case 'google':
      authApiUrl = $useApi.auth.loginWithGoogle;
      signin_method = 'google_sign_btn';
      break;
    case 'apple':
      authApiUrl = $useApi.auth.loginWithApple;
      signin_method = 'apple_sign_btn';
      break;
    case 'email':
      authApiUrl = $useApi.auth.loginWithEmail;
      signin_method = 'email_sign_btn';
  }

  if (!authApiUrl) return;

  try {
    const referral = [refererUser.value, data.ref].find(
      (item) => typeof item === 'string' && item,
    );
    const payload = { ...data };
    delete payload.ref;

    const response = await $fetch(authApiUrl, {
      method: 'POST',
      headers: {
        ...useCustomRequestHeaders(),
        'X-Domain': url.hostname,
      },
      body: {
        ...payload,
        ...(referral ? { ref: referral } : {}),
      },
    });

    applyAuthSession(response, signin_method);

    isOTPScreen.value = false;

    if (props.sendUtmData && props.utmData) {
      props.sendUtmData(props.utmData);
    }

    emit('login');
  } catch (error) {
    if (error.response?.status == 429) {
      emailErrorMessages.value = 'error.email_ban';
    }

    if (error.response?.status === 401 && type === 'email') {
      emailErrorMessages.value = 'error.otp_invalid';
    }
  } finally {
    isLoading.value = false;
  }
}

function backToLoginWindow() {
  isOTPScreen.value = false;
  emailErrorMessages.value = null;
}
</script>

<style lang="scss">
@use '@/assets/styles/components/modal/login-modal.scss' as *;
</style>
